import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LanguageService, UiLanguage } from '../../services/language.service';
import { AdminProject, ProjectPayload, ProjectTranslation } from '../../services/admin-projects.service';

/** The copy fields, in the order the form shows them. One definition drives
 *  every language, so the set can never differ between them. */
interface CopyField {
    key: keyof ProjectTranslation;
    label: string;
    /** Multi-line fields get a textarea. */
    long: boolean;
    required: boolean;
    placeholder: string;
}

/**
 * Add or edit a project (case study).
 *
 * Collects the row and returns it via activeModal.close(payload); the caller
 * does the save and the toast, matching how EquipmentModalComponent is used.
 *
 * Set `project` before opening to edit; leave it null to create.
 *
 * One column of fields with a language switch above it, rather than a column per
 * language: the set of languages comes from LanguageService, so adding one there
 * adds a tab here and needs no layout change. Only English is required — an
 * empty field in another language falls back to the English on the site, which
 * is what an absent `l10n` entry already does.
 */
@Component({
    selector: 'app-project-modal',
    imports: [FormsModule],
    templateUrl: './project-modal.component.html',
    styleUrl: './project-modal.component.scss',
})
export class ProjectModalComponent implements OnInit {
    private languageService = inject(LanguageService);

    /** Set by the caller. null = create. */
    project: AdminProject | null = null;

    /**
     * Every project, in carousel order — set by the caller.
     *
     * Only used to explain what a typed position will do. The server does the
     * actual placement, so a stale list here can misdescribe the outcome but
     * cannot produce a wrong one.
     */
    siblings: AdminProject[] = [];

    readonly fields: CopyField[] = [
        { key: 'title', label: 'Title', long: false, required: true, placeholder: 'Greek parliament' },
        { key: 'description', label: 'Description', long: true, required: true, placeholder: 'What the project delivered' },
        { key: 'problemStatement', label: 'Problem', long: true, required: false, placeholder: 'What the client was up against' },
        { key: 'solution', label: 'Solution', long: true, required: false, placeholder: 'What was installed and why' },
        { key: 'industry', label: 'Industry', long: false, required: false, placeholder: 'Government' },
        { key: 'location', label: 'Location', long: false, required: false, placeholder: 'Athens, Greece' },
    ];

    /** The languages to offer, straight from the app's own list. */
    readonly languages: UiLanguage[] = this.languageService.languages;

    /** Canonical language — its copy lives in the row's own columns, every other
     *  language under `l10n[code]`. */
    private static readonly CANONICAL = 'en';

    /**
     * Copy for every language: copy[code][field].
     *
     * All languages are held at once so switching tabs never loses an unsaved
     * edit, and one save submits the lot.
     */
    copy: Record<string, Record<string, string>> = {};

    activeLang = ProjectModalComponent.CANONICAL;

    year = '';
    displayOrder: number | null = null;

    /** URL key. Derived from the English title until someone edits it. */
    slug = '';
    private slugTouched = false;

    /** Existing photo (signed URL, or the bundled asset path) while editing. */
    currentImage: string | null = null;
    /** Data URL of a newly picked file, shown instead of currentImage. */
    newImagePreview: string | null = null;
    newImageFile: File | null = null;
    imageError = '';
    dragging = false;

    /** Rejected before upload — the server enforces the same rules. */
    private static readonly ACCEPTED = ['png', 'jpg', 'jpeg', 'webp', 'gif'];
    private static readonly MAX_BYTES = 5 * 1024 * 1024;

    submitted = false;

    constructor(public activeModal: NgbActiveModal) { }

    ngOnInit(): void {
        // Reset the picked-file state explicitly, so re-initialising the same
        // instance cannot leak a pending upload into the next project.
        this.newImagePreview = null;
        this.newImageFile = null;
        this.imageError = '';
        this.activeLang = ProjectModalComponent.CANONICAL;

        // Seed a bucket per language up front, so a tab the row has no
        // translation for still has somewhere to type.
        this.copy = {};
        for (const lang of this.languages) {
            this.copy[lang.code] = {};
            for (const field of this.fields) {
                this.copy[lang.code][field.key] = '';
            }
        }

        if (!this.project) return;

        for (const field of this.fields) {
            this.copy[ProjectModalComponent.CANONICAL][field.key] =
                (this.project as any)[field.key] ?? '';
        }
        for (const [code, values] of Object.entries(this.project.translations ?? {})) {
            if (!this.copy[code]) continue;   // a stored language the app no longer offers
            for (const field of this.fields) {
                this.copy[code][field.key] = (values as any)?.[field.key] ?? '';
            }
        }

        this.year = this.project.year ?? '';
        this.displayOrder = this.project.displayOrder;
        this.slug = this.project.slug ?? '';
        // Only stop auto-filling if the row already has one; a project waiting
        // to be backfilled should still get a slug from its title.
        this.slugTouched = !!this.project.slug;
        this.currentImage = this.project.image ?? null;
    }

    get isEdit(): boolean {
        return !!this.project;
    }

    get isCanonical(): boolean {
        return this.activeLang === ProjectModalComponent.CANONICAL;
    }

    /**
     * Keep the slug in step with the English title until it is edited by hand.
     *
     * Only the English title feeds it: a Greek title slugifies to nothing, and
     * the slug exists precisely so the URL does not change with the language.
     */
    onTitleChange(): void {
        if (this.slugTouched || !this.isCanonical) return;
        this.slug = this.slugify(this.copy[ProjectModalComponent.CANONICAL]?.['title'] || '');
    }

    onSlugInput(): void {
        this.slugTouched = true;
    }

    slugify(value: string): string {
        return (value || '')
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    get slugInvalid(): boolean {
        return this.submitted && this.slugify(this.slug) === '';
    }

    /** The slug is the public address, so changing it breaks existing links. */
    get slugChanged(): boolean {
        return this.isEdit
            && !!this.project?.slug
            && this.slugify(this.slug) !== this.project.slug;
    }

    /** What the preview shows: a newly picked file wins over the stored photo. */
    get previewUrl(): string | null {
        return this.newImagePreview ?? this.currentImage;
    }

    get imageName(): string {
        return this.newImageFile?.name ?? '';
    }

    /**
     * What the typed position will do, in words.
     *
     * Typing a position that is taken inserts the project there and shifts the
     * rest down — the same rule as dragging a row — so say so rather than
     * letting it look like two projects will share a number.
     */
    get orderNote(): string | null {
        const others = this.siblings.filter((p) => p.id !== this.project?.id);
        const total = others.length + 1;

        if (this.displayOrder === null || (this.displayOrder as any) === '') {
            return this.isEdit
                ? 'Leave blank to keep its current position.'
                : `Leave blank to add it last, at position ${total}.`;
        }

        const wanted = Number(this.displayOrder);
        if (!Number.isFinite(wanted) || wanted < 1) return null;

        // Unchanged position on an edit: nothing moves.
        if (this.isEdit && this.project?.displayOrder === wanted) {
            return 'Unchanged — nothing else moves.';
        }

        if (wanted >= total) {
            return `Goes last, at position ${total}.`;
        }

        // Whoever holds that position now gets pushed down, along with
        // everything after them.
        const occupant = others[wanted - 1];
        const after = total - wanted;
        const shifted = after === 1 ? '1 project' : `${after} projects`;
        return occupant
            ? `Inserted at ${wanted}, ahead of "${occupant.title}". ${shifted} shift down one.`
            : `Inserted at ${wanted}. ${shifted} shift down one.`;
    }

    /** A photo is mandatory on create — the carousel has nothing to show
     *  without one, and `imageUrl` is a required column. */
    get imageMissing(): boolean {
        return this.submitted && !this.isEdit && !this.newImageFile;
    }

    /** True once any field in this language is filled, so the tabs can mark the
     *  ones still untranslated. */
    hasCopy(code: string): boolean {
        const values = this.copy[code] ?? {};
        return this.fields.some((f) => (values[f.key] || '').trim() !== '');
    }

    /** Required fields are only enforced for the canonical language: the rest
     *  fall back to it when left blank. */
    invalid(key: string): boolean {
        if (!this.isCanonical) return false;
        const field = this.fields.find((f) => f.key === key);
        const value = this.copy[ProjectModalComponent.CANONICAL]?.[key] || '';
        return this.submitted && !!field?.required && value.trim() === '';
    }

    /** The English text for a field, shown as a hint on the other tabs so the
     *  translator can see what they are translating. */
    canonicalValue(key: string): string {
        return (this.copy[ProjectModalComponent.CANONICAL]?.[key] || '').trim();
    }

    placeholderFor(field: CopyField): string {
        return this.isCanonical ? field.placeholder : 'Leave blank to show the English';
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.dragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.dragging = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragging = false;
        const file = event.dataTransfer?.files?.[0];
        if (file) this.acceptFile(file);
    }

    onFilePicked(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) this.acceptFile(file);
        // reset so re-picking the same file fires change again
        input.value = '';
    }

    /** Validates and reads the file into a data URL for the preview. */
    private acceptFile(file: File): void {
        this.imageError = '';
        const suffix = (file.name.split('.').pop() || '').toLowerCase();
        if (!ProjectModalComponent.ACCEPTED.includes(suffix)) {
            this.imageError = `Unsupported type ".${suffix}". Use ${ProjectModalComponent.ACCEPTED.join(', ')}.`;
            return;
        }
        if (file.size > ProjectModalComponent.MAX_BYTES) {
            this.imageError = `That image is ${(file.size / 1024 / 1024).toFixed(1)}MB. The limit is 5MB.`;
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            this.newImagePreview = reader.result as string;
            this.newImageFile = file;
        };
        reader.onerror = () => { this.imageError = 'That file could not be read.'; };
        reader.readAsDataURL(file);
    }

    /** Drops the picked file. An existing stored photo cannot be cleared — see
     *  the service's note on why there is no remove case. */
    clearPicked(): void {
        this.imageError = '';
        this.newImagePreview = null;
        this.newImageFile = null;
    }

    save(): void {
        this.submitted = true;

        const canonical = this.copy[ProjectModalComponent.CANONICAL] ?? {};
        const title = (canonical['title'] || '').trim();
        const description = (canonical['description'] || '').trim();
        if (!title || !description) {
            // The offending fields are on the English tab, which may not be the
            // one showing — switch to it rather than flagging nothing.
            this.activeLang = ProjectModalComponent.CANONICAL;
            return;
        }
        const slug = this.slugify(this.slug);
        if (!slug) return;
        if (!this.isEdit && !this.newImageFile) return;

        const trim = (code: string, key: string): string | null => {
            const value = (this.copy[code]?.[key] || '').trim();
            return value === '' ? null : value;
        };

        // Every non-canonical language, whether or not it has copy: an emptied
        // field has to reach the server so it can drop the stored translation.
        const translations: Record<string, ProjectTranslation> = {};
        for (const lang of this.languages) {
            if (lang.code === ProjectModalComponent.CANONICAL) continue;
            translations[lang.code] = {
                title: trim(lang.code, 'title'),
                description: trim(lang.code, 'description'),
                problemStatement: trim(lang.code, 'problemStatement'),
                solution: trim(lang.code, 'solution'),
                industry: trim(lang.code, 'industry'),
                location: trim(lang.code, 'location'),
            };
        }

        const payload: ProjectPayload = {
            slug,
            title,
            description,
            problemStatement: trim(ProjectModalComponent.CANONICAL, 'problemStatement'),
            solution: trim(ProjectModalComponent.CANONICAL, 'solution'),
            industry: trim(ProjectModalComponent.CANONICAL, 'industry'),
            location: trim(ProjectModalComponent.CANONICAL, 'location'),
            // Not one of `fields` — it is the same in every language, which is
            // why the public endpoint reads it with .get() rather than .l10n().
            year: this.year.trim() || null,
            displayOrder: this.displayOrder === null || (this.displayOrder as any) === ''
                ? null
                : Number(this.displayOrder),
            translations,
        };

        if (this.newImagePreview && this.newImageFile) {
            payload.imageBase64 = this.newImagePreview;
            payload.imageFilename = this.newImageFile.name;
            payload.imageContentType = this.newImageFile.type;
        }
        // Not set on an edit: the request carries no image instruction and the
        // server leaves the existing photo untouched.

        this.activeModal.close(payload);
    }
}
